import { Events } from '../events';
import { AuthHttp, JwtHelper, tokenNotExpired } from 'angular2-jwt';
import { Injectable, Inject } from '@angular/core';
import { Http, Headers, RequestOptions, URLSearchParams, Response } from '@angular/http'
import { Observable } from 'rxjs/Rx';
import { CustomerModel } from '../customer/customer.model';
import { BaseService } from '../base.service';
import { AuthEvents } from './auth-events';
import { AuthStorageService } from './auth-storage.service';

@Injectable()
export class AuthService extends BaseService {
  get user(): CustomerModel {
    return this._user;
  }

  private _user: CustomerModel = null;

  // TODO: create a configuration service
  //authServerUrl: string = 'http://ec2-54-213-237-204.us-west-2.compute.amazonaws.com:8080/';
  //resourceServerUrl: string = 'http://ec2-54-213-237-204.us-west-2.compute.amazonaws.com:8080/';
  //authServerUrl: string = 'http://localhost:8080/';//http://localhost:8081/'
  //resourceServerUrl: string = 'http://localhost:9090/';
  //dispacher: String ='http://localhost:8081/';
  //resourceServerUrl: string = 'http://10.0.1.161:9090/';
  //private _appSecret: string = 'R2FuYWxvdHRvZG86c2VjcmV0';
  private _jwtHelper: JwtHelper = new JwtHelper();
  private _refreshSubscription: any;
  private _isReady: boolean = false;

  constructor(
    private _http: Http,
    private _authHttp: AuthHttp,
    private _storage: AuthStorageService,
    private _events: Events) {
    super();

  }

  authenticated(): Promise<boolean> {
    // Check if there is a valid JWT
    return this._storage.getAccessToken().then(token => {
      if (!token)
        return Promise.resolve(false);

      return !!tokenNotExpired(null, token);
    });
  }

  login(credentials: any): Observable<void> {
    let headers = new Headers();
    headers.append('Content-Type', 'application/x-www-form-urlencoded');
    //headers.append('Authorization', 'Basic ' + this._appSecret);

    let options = new RequestOptions({ headers: headers });

    let payload = new URLSearchParams();
    payload.set('grant_type', 'password');
    payload.set('username', credentials.username);
    payload.set('password', credentials.password);
    //payload.set('scope', 'offline_access openid profile');

    return this._http.post(this.urls.token, payload.toString(), options)
      .mergeMap(res => this._processTokenResponse(res.json(), false))
      .catch(err => this.handleErrorAsObservable(err));
  }

  signup(user: any): Observable<void> {
    return this._authHttp.post(this.urls.signup, user)
      .catch(err => this.handleErrorAsObservable(err));
  }

  logout(emitEvent: boolean = true): Observable<void> {
    return Observable.fromPromise(this._storage.removeAuthInfo())
      .do(() => {
        console.log('do next');
        this._user = null;
        if (emitEvent)
          this._events.publish(AuthEvents.USER_LOGOUT);

        this._unscheduleTokenRefresh()
      })
      .catch(err => this.handleErrorAsObservable(err));
  }

  requestResetPassword(model: any) {
    return this._authHttp.post(this.urls.requestResetPassword, model)
      .catch(err => this.handleErrorAsObservable(err));
  }

  setUserInfo(user: CustomerModel) {
    this._user = user;
    return Observable.fromPromise(this._storage.setUser(JSON.stringify(this._user)));
  }

  ready(): Promise<boolean> {
    if (this._isReady)
      return this.authenticated();

    // Check if there is an authenticated user
    return this.authenticated().then((isAuthenticated) => {
      if (isAuthenticated) {
        return this._storage.getUser().then(profile => {
          this._user = JSON.parse(profile)
          this._events.publish(AuthEvents.USER_SIGNIN);
          this._scheduleTokenRefresh();

          return isAuthenticated;
        });
      }
      else {
        return this._storage.getRefreshToken().then(refreshToken => {
          if (refreshToken)
            return this._requestNewJwt().then(() => true);

          return isAuthenticated;
        });
      }
    })
      .then(isAuthenticated => {
        this._isReady = true
        return isAuthenticated;
      })
      .catch(err => {
        console.log(err);

        this._user = null;
        this._isReady = true
        return false;
      });
  }

  private _unscheduleTokenRefresh() {
    // Unsubscribe from the refresh
    if (this._refreshSubscription)
      this._refreshSubscription.unsubscribe();
  }

  private _requestNewJwt(): Promise<any> {
    // Get a new JWT using the refresh token saved in local storage
    console.log('refreshing access token')
    return this._storage.getRefreshToken().then(refreshToken => {
      let headers = new Headers();
      headers.append('Content-Type', 'application/x-www-form-urlencoded');
      //headers.append('Authorization', 'Basic ' + this._appSecret);

      let options = new RequestOptions({ headers: headers });

      let payload = new URLSearchParams();
      payload.set('grant_type', 'refresh_token');
      payload.set('refresh_token', refreshToken);

      return this._http.post(this.urls.token, payload.toString(), options)
        .mergeMap(res => this._processTokenResponse(res.json(), true)).toPromise();
    });
  }

  private _processTokenResponse(authResponse: any, isRefreshTokenResponse: boolean): Observable<void> {
    if (!authResponse.access_token || this._jwtHelper.isTokenExpired(authResponse.access_token)) {
      return Observable.throw('Invalid token');
    }

    return Observable.fromPromise(this._storage.setAccessToken(authResponse.access_token))
      .mergeMap(() => {
        if (!isRefreshTokenResponse) {
          let claims: any = this._jwtHelper.decodeToken(authResponse.access_token);

          return this.getUserInfo()
            .mergeMap((customer) => {
              this._user = customer;
              return Observable.fromPromise(this._storage.setUser(JSON.stringify(this._user)));
            });
        }
        else {
          return Observable.fromPromise(this._storage.getUser()
            .then(profile => {
              this._user = JSON.parse(profile)
            }));
        }
      })
      .mergeMap(() => this._storage.setRefreshToken(authResponse.refresh_token))
      .do(() => {
        this._scheduleTokenRefresh();
        this._events.publish(AuthEvents.USER_SIGNIN);
      })
      .catch(err => {
        // Remove session data stored so far, but don't notify a logout event
        this.logout(/*emitEvent*/ false).subscribe();
        return Observable.throw(err);
      });
  }

  private getUserInfo(): Observable<CustomerModel> {
    return this._authHttp.get(this.urls.customer)
      .map(response => response.json())
  }

  private _scheduleTokenRefresh() {
    // Unsubscribe from previous refresh schedule
    this._unscheduleTokenRefresh();

    let source = Observable.fromPromise(this._storage.getAccessToken()).mergeMap(
      token => {
        let delay = this._getAccessTokenRefreshDelay(token);
        return Observable.timer(delay);
      });

    this._refreshSubscription = source.subscribe(() => {
      this._requestNewJwt();
    });
  }

  /**
   * Gets the delay in milliseconds to refresh the access token
   * @param token JWT access token
   */
  private _getAccessTokenRefreshDelay(token: string): number {
    let now: number = new Date().valueOf();
    let jwtExp: number = this._jwtHelper.decodeToken(token).exp;
    let exp: Date = new Date(0);

    exp.setUTCSeconds(jwtExp);
    return exp.valueOf() - now;
  }
}




// WEBPACK FOOTER //
// /jenkins/jenkins/workspace/apppronosticos_install_s3_dev/src/app/core/auth/auth.service.ts